import type { Scene } from '@lumen5/luminary';

import {
    TRANSITION_LABELS,
    TRANSITION_URL_DATA,
} from 'js/src/components/app/transitions-sidebar/transition-data';
import {
    hasAppliedMatchCutWithNextScene,
    hasAppliedTransition,
} from 'js/src/luminary/luminary-selectors';

/** The transition `set_scene_transition` offers for a match cut, and how the video state names it. */
export const MATCH_CUT = 'Match cut';

/** A seam a pre-built scene sequence animates as it was designed to. Nothing can change it. */
export const SCENE_SEQUENCE = 'scene sequence';

/** A seam an animated zoom animates. Nothing but the zoom itself can change it. */
export const ANIMATED_ZOOM = 'animated zoom';

/** A transition that came with the scene's design rather than from the transitions catalog. */
export const DESIGN_TRANSITION = "the scene design's own transition";

/** A catalog transition named as `set_scene_transition` takes it, e.g. "Push (Up)". */
export function describeCatalogTransition(transitionUrl: string): string | undefined {
    const transition = TRANSITION_URL_DATA[transitionUrl];
    return transition && `${TRANSITION_LABELS[transition.groupId]} (${transition.optionId})`;
}

/**
 * How a scene is entered from the one before it, or undefined for a straight cut (and for the first
 * scene). A seam holds one thing, though Luminary keeps them on different sides of it: a match cut,
 * a scene sequence and an animated zoom on the scene before, a transition on the scene itself.
 */
export function describeTransitionInto(scene: Scene): string | undefined {
    const prevScene = scene.prevScene;
    if (!prevScene) {
        return undefined;
    }
    if (prevScene.isPrePackagedMorphWithNextScene) {
        return SCENE_SEQUENCE;
    }
    if (hasAppliedMatchCutWithNextScene(prevScene)) {
        return MATCH_CUT;
    }
    if (prevScene.morphEnabled) {
        return ANIMATED_ZOOM;
    }
    if (!hasAppliedTransition(scene)) {
        return undefined;
    }
    return (
        describeCatalogTransition(scene.customTransition.transitionUrl ?? '') ?? DESIGN_TRANSITION
    );
}
